import React from "react";
import { observer } from "mobx-react";
import { DialogCustomisationSettings } from "../DialogCustomisationSettings";
import { useTranslation } from "react-i18next";
import {
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { useBorder } from "@pulse/shared-components";
import { useSurveyStore } from "../../../../store/Hooks";
import { Stack } from "@mui/material";
import { InfoDialogCustomisationSettings } from "../InfoDialogCustomisationSettings";
import { SurveySettingsTabContentHeader } from "../SurveySettingsTabContentHeader";
import { SurveySettingsTab } from "../SurveySettingsPaneTab";

export const SurveySettingsDialogCustomizationTab: React.FC = observer(() => {
  const { t } = useTranslation();
  const spacing = useSpacing();
  const typography = useTypography();
  const tokens = useFoundationColorTokens();
  const border = useBorder();
  const surveyStore = useSurveyStore();

  const updatedBackOfficeSurveySettings =
    surveyStore.backOfficeSurveySettingsStore.updatedBackOfficeSurveySettings;
  const initialBackOfficeSurveySettings =
    surveyStore.backOfficeSurveySettingsStore.initialBackOfficeSurveySettings;

  if (!updatedBackOfficeSurveySettings || !initialBackOfficeSurveySettings) {
    return;
  }

  const updatedBackOfficeSurveySettingsUIStore =
    updatedBackOfficeSurveySettings.backOfficeSurveySettingsUIStore;

  return (
    <Stack id={SurveySettingsTab.DialogCustomization}>
      <SurveySettingsTabContentHeader
        title={t(
          "surveys.settings.dialogCustomizationSectionLabel",
          "Dialog Customization",
        )}
      />
      <DialogCustomisationSettings
        checkBoxProps={{
          settingsCheckboxLabelText: t(
            "surveys.settings.surveySubmissionCustomisationSettings.submitConfirmationCheckboxLabelText",
          ),
          settingsCheckboxDescriptionText: t(
            "surveys.settings.surveySubmissionCustomisationSettings.submitConfirmationCheckboxDescriptionText",
          ),
          isCheckboxSelected:
            updatedBackOfficeSurveySettings.isSubmitConfirmationRequired,
          setIsCheckboxSelected:
            updatedBackOfficeSurveySettings.setIsSubmitConfirmationRequired,
        }}
        settingsTitleText={t(
          "surveys.settings.surveySubmissionCustomisationSettings.surveySubmissionCustomisationTitleText",
        )}
        surveySettingsDialogModel={
          updatedBackOfficeSurveySettings.submitConfirmationDialogProperties
        }
        isSecondaryButtonPresent={true}
        t={t}
        spacing={spacing}
        typography={typography}
        tokens={tokens}
        border={border}
        clearError={updatedBackOfficeSurveySettingsUIStore.clearError}
        isSaveButtonClicked={
          updatedBackOfficeSurveySettingsUIStore.isSaveButtonClicked
        }
      />
      <DialogCustomisationSettings
        checkBoxProps={{
          settingsCheckboxLabelText: t(
            "surveys.settings.surveyControlPreferencesSettings.promptUnansweredQuestionsLabelText",
          ),
          settingsCheckboxDescriptionText: t(
            "surveys.settings.surveyControlPreferencesSettings.promptUnansweredQuestionsDescriptionText",
          ),
          isCheckboxSelected:
            updatedBackOfficeSurveySettings.promptUnansweredQuestions,
          setIsCheckboxSelected:
            updatedBackOfficeSurveySettings.setPromptUnansweredQuestions,
        }}
        settingsTitleText={t(
          "surveys.settings.promptUnansweredQuestionsDialogCustomisationSettings.promptUnansweredQuestionsDialogCustomisationTitleText",
        )}
        surveySettingsDialogModel={
          updatedBackOfficeSurveySettings.promptUnansweredQuestionsDialogProperties
        }
        isSecondaryButtonPresent={true}
        t={t}
        spacing={spacing}
        typography={typography}
        tokens={tokens}
        border={border}
        clearError={updatedBackOfficeSurveySettingsUIStore.clearError}
        isSaveButtonClicked={
          updatedBackOfficeSurveySettingsUIStore.isSaveButtonClicked
        }
      />
      <DialogCustomisationSettings
        checkBoxProps={{
          settingsCheckboxLabelText: t(
            "surveys.settings.surveyRestartCustomisationSettings.allowRestartCheckboxLabelText",
          ),
          settingsCheckboxDescriptionText: t(
            "surveys.settings.surveyRestartCustomisationSettings.allowRestartCheckboxDescriptionText",
          ),
          isCheckboxSelected: updatedBackOfficeSurveySettings.isRestartAllowed,
          setIsCheckboxSelected:
            updatedBackOfficeSurveySettings.setIsRestartAllowed,
        }}
        settingsTitleText={t(
          "surveys.settings.surveyRestartCustomisationSettings.surveyRestartCustomisationTitleText",
        )}
        surveySettingsDialogModel={
          updatedBackOfficeSurveySettings.restartSurveyDialogProperties
        }
        isSecondaryButtonPresent={true}
        t={t}
        spacing={spacing}
        typography={typography}
        tokens={tokens}
        border={border}
        clearError={updatedBackOfficeSurveySettingsUIStore.clearError}
        isSaveButtonClicked={
          updatedBackOfficeSurveySettingsUIStore.isSaveButtonClicked
        }
      />
      <DialogCustomisationSettings
        checkBoxProps={{
          settingsCheckboxLabelText: t(
            "surveys.settings.saveDraftCustomisationSettings.allowSaveDraftCheckboxLabelText",
          ),
          settingsCheckboxDescriptionText: t(
            "surveys.settings.saveDraftCustomisationSettings.allowSaveDraftCheckboxDescriptionText",
          ),
          isCheckboxSelected:
            updatedBackOfficeSurveySettings.isSaveDraftAllowed,
          setIsCheckboxSelected:
            updatedBackOfficeSurveySettings.setIsSaveDraftAllowed,
        }}
        settingsTitleText={t(
          "surveys.settings.saveDraftCustomisationSettings.saveDraftCustomisationTitleText",
        )}
        surveySettingsDialogModel={
          updatedBackOfficeSurveySettings.saveDraftDialogProperties
        }
        isSecondaryButtonPresent={false}
        t={t}
        spacing={spacing}
        typography={typography}
        tokens={tokens}
        border={border}
        clearError={updatedBackOfficeSurveySettingsUIStore.clearError}
        isSaveButtonClicked={
          updatedBackOfficeSurveySettingsUIStore.isSaveButtonClicked
        }
      />
      <DialogCustomisationSettings
        settingsTitleText={t(
          "surveys.settings.surveyAlreadySubmittedDialogCustomisationSettings.surveyAlreadySubmittedDialogCustomisationTitleText",
        )}
        surveySettingsDialogModel={
          updatedBackOfficeSurveySettings.surveySettings
            .surveyAlreadySubmittedDialogTexts
        }
        isSecondaryButtonPresent={false}
        t={t}
        spacing={spacing}
        typography={typography}
        tokens={tokens}
        border={border}
        clearError={updatedBackOfficeSurveySettingsUIStore.clearError}
        isSaveButtonClicked={
          updatedBackOfficeSurveySettingsUIStore.isSaveButtonClicked
        }
      />
      <DialogCustomisationSettings
        settingsTitleText={t(
          "surveys.settings.languageSelectionDialogCustomisationSettings.languageSelectionDialogCustomisationTitleText",
        )}
        surveySettingsDialogModel={
          updatedBackOfficeSurveySettings.surveySettings
            .languageSelectionDialogTexts
        }
        isSecondaryButtonPresent={true}
        t={t}
        spacing={spacing}
        typography={typography}
        tokens={tokens}
        border={border}
        clearError={updatedBackOfficeSurveySettingsUIStore.clearError}
        isSaveButtonClicked={
          updatedBackOfficeSurveySettingsUIStore.isSaveButtonClicked
        }
      />
      <InfoDialogCustomisationSettings
        isInfoButtonShown={updatedBackOfficeSurveySettings.isInfoButtonShown}
        setIsInfoButtonShown={
          updatedBackOfficeSurveySettings.setIsInfoButtonShown
        }
        infoDialogDescription={
          initialBackOfficeSurveySettings.surveyInformationDialogProperties
            .descriptionText
        }
        setInfoDialogDescription={
          updatedBackOfficeSurveySettings.surveyInformationDialogProperties
            .setDescriptionText
        }
        isInfoDescriptionErrored={updatedBackOfficeSurveySettingsUIStore.isInfoDialogDescriptionErrored(
          updatedBackOfficeSurveySettings.surveyInformationDialogProperties
            .descriptionText,
        )}
        clearError={updatedBackOfficeSurveySettingsUIStore.clearError}
        t={t}
        spacing={spacing}
        typography={typography}
        tokens={tokens}
        border={border}
        rteFileService={
          updatedBackOfficeSurveySettings.infoDialogDescriptionTextRTEFileService
        }
        updatedText={
          updatedBackOfficeSurveySettings.surveyInformationDialogProperties
            .descriptionText
        }
      />
    </Stack>
  );
});
